import {
  driverAssignmentDetailColumns,
  driverAssignmentDetailDefaultColumns,
  driverAssignmentDetailSpecificFilters,
  driverAssignmentDetailSummaryFields,
} from '@cabgo/shared';
import type { ReportViewDefinition } from '../reportViewTypes';

export const driverAssignmentDetailView: ReportViewDefinition = {
  key: 'driver_assignment_detail',
  titleKey: 'da_title',
  descriptionKey: 'da_description',
  dateBasisKey: 'da_date_basis',
  columns: driverAssignmentDetailColumns,
  defaultColumns: driverAssignmentDetailDefaultColumns,
  dateMode: 'range',
  supportsSearch: true,
  defaultSort: 'eventAt',
  sorts: ['eventAt'],
  filters: driverAssignmentDetailSpecificFilters.map((filter) => ({
    ...filter,
    labelKey: `da_filter_${filter.id}`,
    ...('values' in filter
      ? { options: filter.values.map((value) => ({ value, labelKey: `da_value_${value}` })) }
      : {}),
  })),
  valueLabels: {
    assignment_event_kind: { ASSIGNED: 'da_value_ASSIGNED', REASSIGNED: 'da_value_REASSIGNED' },
    row_kind: {
      OFFER: 'da_value_OFFER',
      ASSIGNMENT_EVENT: 'da_value_ASSIGNMENT_EVENT',
    },
    offer_kind: {
      LEGACY_HISTORY: 'da_value_LEGACY_HISTORY',
      AUTO_INITIAL: 'da_value_AUTO_INITIAL',
      MANUAL_INITIAL: 'da_value_MANUAL_INITIAL',
      MANUAL_REASSIGNMENT: 'da_value_MANUAL_REASSIGNMENT',
    },
    offer_status: {
      PENDING: 'da_value_PENDING',
      ACCEPTED: 'da_value_ACCEPTED',
      REJECTED: 'da_value_REJECTED',
      EXPIRED: 'da_value_EXPIRED',
      SUPERSEDED: 'da_value_SUPERSEDED',
      CANCELLED: 'da_value_CANCELLED',
    },
    dispatch_mode: {
      AUTO: 'da_value_AUTO',
      MANUAL: 'da_value_MANUAL',
    },
    strategy: {
      SEQUENTIAL: 'da_value_SEQUENTIAL',
      BROADCAST: 'da_value_BROADCAST',
    },
    search_purpose: {
      INITIAL: 'da_value_INITIAL',
      REASSIGNMENT: 'da_value_REASSIGNMENT',
    },
    evidence_state: {
      LEGACY_HISTORY: 'da_value_LEGACY_HISTORY',
      AVAILABLE: 'da_value_AVAILABLE',
      MISSING: 'da_value_MISSING',
      EXPIRED: 'da_value_EXPIRED',
      UNSUPPORTED: 'da_value_UNSUPPORTED',
    },
    search_state: {
      ACTIVE: 'da_value_ACTIVE',
      ASSIGNED: 'da_value_ASSIGNED',
      EXHAUSTED: 'da_value_EXHAUSTED',
      CANCELLED: 'da_value_CANCELLED',
      SUPERSEDED: 'da_value_SUPERSEDED',
      LEGACY_HISTORY: 'da_value_LEGACY_HISTORY',
    },
    date_basis: {
      OFFERED_AT: 'da_value_OFFERED_AT',
      ASSIGNMENT_EVENT: 'da_value_ASSIGNMENT_EVENT',
    },
    service_date_basis: {
      SCHEDULED_START: 'da_value_SCHEDULED_START',
      CREATED_AT: 'da_value_CREATED_AT',
    },
    attempt_profile: {
      NOW: 'da_value_NOW',
      SCHEDULED: 'da_value_SCHEDULED',
    },
    attempt_trigger: {
      INITIAL: 'da_value_INITIAL',
      RETRY: 'da_value_RETRY',
      REFILL: 'da_value_REFILL',
      MANUAL_REFRESH: 'da_value_MANUAL_REFRESH',
    },
    policy_source: {
      SYSTEM: 'da_value_SYSTEM',
      PROVIDER: 'da_value_PROVIDER',
      AREA: 'da_value_AREA',
      MIXED: 'da_value_MIXED',
    },
    confirmation_actor_type: {
      AGENT: 'da_value_AGENT',
      ORG_USER: 'da_value_ORG_USER',
      PASSENGER: 'da_value_PASSENGER',
      SYSTEM: 'da_value_SYSTEM',
    },
    confirmation_outcome: {
      STANDALONE_CONFIRMATION: 'da_value_STANDALONE_CONFIRMATION',
      LINKED_CONFIRMATION: 'da_value_LINKED_CONFIRMATION',
      ACCEPTED_WITHOUT_CONFIRMATION_EVENT: 'da_value_ACCEPTED_WITHOUT_CONFIRMATION_EVENT',
      NO_CONFIRMATION: 'da_value_NO_CONFIRMATION',
    },
    scope_provenance: {
      CURRENT_SERVICE_GROUP: 'da_value_CURRENT_SERVICE_GROUP',
    },
    trip_status: {
      SCHEDULED: 'da_value_SCHEDULED',
      UNASSIGNED: 'da_value_UNASSIGNED',
      ASSIGNED: 'da_value_ASSIGNED',
      AWAITING_PAYMENT: 'da_value_AWAITING_PAYMENT',
      EN_ROUTE_PICKUP: 'da_value_EN_ROUTE_PICKUP',
      ARRIVED_PICKUP: 'da_value_ARRIVED_PICKUP',
      IN_PROGRESS: 'da_value_IN_PROGRESS',
      PICKED_UP: 'da_value_PICKED_UP',
      ARRIVED_DROPOFF: 'da_value_ARRIVED_DROPOFF',
      COMPLETED: 'da_value_COMPLETED',
      CANCELLED: 'da_value_CANCELLED',
      MISSED: 'da_value_MISSED',
      AWAITING_OVERAGE: 'da_value_AWAITING_OVERAGE',
    },
  },
  summary: driverAssignmentDetailSummaryFields.map((field) => ({
    field,
    labelKey: `da_summary_${field}`,
    unit: 'count',
  })),
  primarySummary: [
    'rowCount',
    'offerCount',
    'acceptedOfferCount',
    'rejectedOfferCount',
    'expiredOfferCount',
    'confirmedCount',
  ],
  summaryGroups: [
    { labelKey: 'summaryGroupOverview', fields: ['rowCount', 'distinctTripCount'] },
    {
      labelKey: 'summaryGroupDispatch',
      fields: [
        'offerCount',
        'acceptedOfferCount',
        'rejectedOfferCount',
        'expiredOfferCount',
        'supersededOfferCount',
        'cancelledOfferCount',
        'pendingOfferCount',
        'confirmationOnlyCount',
        'manualCount',
        'reassignmentCount',
        'confirmedCount',
      ],
    },
    { labelKey: 'summaryGroupEvidence', fields: ['ambiguousConfirmationCount'] },
  ],
};
